import { PlayIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { NOTHING_RELEASED, RunOptionsControls, runTargetsBlocked, type RunTargets, type RunToolbarOptions } from './RunOptionsControls';

export type { RunToolbarOptions } from './RunOptionsControls';
export { CONCURRENCY_CHOICES, environmentTargetLabel } from './RunOptionsControls';

interface RunToolbarProps {
  selectedCount: number;
  options: RunToolbarOptions;
  onOptionsChange: (options: RunToolbarOptions) => void;
  /** The run target rule's inputs (task 1236); null while the versioning feature is off. */
  targets?: RunTargets | null;
  onRunSelected: () => void;
  onClear: () => void;
  running: boolean;
  /**
   * How many selected scenarios are already running in a test on the chosen
   * target (task 1229). Above zero, Run selected is disabled with the reason in
   * its title, and a line under the toolbar says how many.
   */
  inFlightCount?: number;
  /** Extra controls (Save as suite — task 1180). */
  children?: React.ReactNode;
}

/**
 * The selection toolbar (task 1176), summoned by checking test rows: a
 * freeform test run of the selection. Where it goes and how many tests run at
 * once come from the shared `RunOptionsControls` (task 1209); there is no Run
 * all — the header "select all" plus Run selected covers it. Every run creates
 * one test run, graded against whatever criteria exist (task 1185).
 */
export function RunToolbar({
  selectedCount,
  options,
  onOptionsChange,
  targets,
  onRunSelected,
  onClear,
  running,
  inFlightCount = 0,
  children,
}: RunToolbarProps) {
  const blocked = inFlightCount > 0;
  const reason = blocked
    ? `${inFlightCount} of the selected ${inFlightCount === 1 ? 'scenario is' : 'scenarios are'} already running in a test on this target`
    : undefined;
  // Nothing released anywhere (task 1236): there is no target to run on.
  const nothingReleased = runTargetsBlocked(targets);
  return (
    <div className="border-b border-indigo-200 dark:border-indigo-900 bg-indigo-50 dark:bg-indigo-950/40">
    <div
      data-testid="run-toolbar"
      className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-2.5"
    >
      <span className="text-xs font-medium text-indigo-900 dark:text-indigo-200">
        {selectedCount} selected
      </span>

      <RunOptionsControls options={options} onOptionsChange={onOptionsChange} targets={targets} />

      {children}

      <span className="ml-auto flex items-center gap-1.5">
        <button
          type="button"
          onClick={onClear}
          className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
        >
          <XMarkIcon className="h-3.5 w-3.5 mr-1" />
          Clear
        </button>
        <button
          type="button"
          onClick={onRunSelected}
          disabled={running || selectedCount === 0 || blocked || nothingReleased}
          title={nothingReleased ? NOTHING_RELEASED : reason}
          className="inline-flex items-center px-3 py-1 text-xs font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
        >
          <PlayIcon className="h-3.5 w-3.5 mr-1" />
          {`Run selected (${selectedCount})`}
        </button>
      </span>
    </div>
    {blocked && (
      <p data-testid="run-toolbar-in-flight" className="px-4 pb-2 text-xs text-indigo-900 dark:text-indigo-200">
        {reason}. Clear {inFlightCount === 1 ? 'it' : 'them'} from the selection, wait for that test to finish, or pick another target.
      </p>
    )}
    </div>
  );
}

export default RunToolbar;
